import { useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

import { ErrorView } from "@/components/_molecules/error-view";
import { Loading } from "@/components/_molecules/loading-view";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useCurrentWorkspaceContext } from "@/domain/workspace/hooks/useCurrentWorkspaceContext";
import { useDocumentTitle } from "@/hooks/use-document-title";

import { useGetDataRequestEntityQuery } from "../../hooks/api/use-get-data-request-entity";
import { useSubmitDataRequestEntityMutation } from "../../hooks/api/use-submit-data-request-entity";
import { buildEntityHref } from "../../lib/build-entity-href";
import { isInvalidDateRangeError } from "../../lib/is-invalid-date-range-error";
import { toFieldResponseMap } from "../../lib/to-field-response-map";
import { type EntityMode } from "../create-data-request-dialog/create-data-request-state";
import { type DataRequestFieldDefinition, getAvailableFields } from "../create-data-request-dialog/data-request-fields";

import { FieldDataEntry } from "./field-data-entry";
import { useAutoSave } from "./use-auto-save";
import { clearDraftMap } from "./use-draft-persistence";

export function DataEntryPage() {
  const { dataRequestEntityId } = useParams<{ dataRequestEntityId: string }>();
  const { t } = useDomainTranslation("data-request");
  const { currentWorkspace } = useCurrentWorkspaceContext();
  const navigate = useNavigate();
  const { dataRequestEntity, loading, error } = useGetDataRequestEntityQuery({
    id: dataRequestEntityId,
  });
  useDocumentTitle(t("dataEntry.title"));

  const fieldValues = dataRequestEntity?.currentFieldValues ?? null;

  const { saveField, saveFreeText, flush } = useAutoSave({
    dataRequestEntityId: dataRequestEntityId ?? "",
  });

  const { submitEntity, loading: submitting } = useSubmitDataRequestEntityMutation({
    onCompleted: () => {
      if (dataRequestEntityId) {
        clearDraftMap(dataRequestEntityId);
      }
      toast.success(t("dataEntry.submitted"));
      navigate(-1);
    },
    onError: (error) => {
      if (isInvalidDateRangeError(error)) {
        toast.error(t("common:errors.invalidDateRangeTitle"), { description: t("common:errors.invalidDateRangeDescription") });
        return;
      }
      toast.error(t("dataEntry.submitError"));
    },
  });

  const isReadOnly =
    dataRequestEntity?.status === "COMPLETED" || dataRequestEntity?.dataRequest?.status === "CLOSED";

  const documentsRequested = dataRequestEntity?.dataRequest?.enableDocuments === true;

  const freeTextQuestions = dataRequestEntity?.dataRequest?.freeTextQuestions ?? [];

  const fieldResponseMap = useMemo(
    () => toFieldResponseMap(dataRequestEntity?.fieldResponses),
    [dataRequestEntity],
  );

  const handleSubmit = useCallback(async () => {
    if (!dataRequestEntityId) return;
    await flush();
    submitEntity(dataRequestEntityId);
  }, [dataRequestEntityId, flush, submitEntity]);

  const visibleFieldList = useMemo((): DataRequestFieldDefinition[] => {
    const entityType = dataRequestEntity?.entityType as EntityMode | undefined;
    const availableFields = getAvailableFields(entityType ? [entityType] : ["CLIMATE_ACTION", "PROJECT"]);
    const fieldSelection = dataRequestEntity?.dataRequest?.fieldSelection;
    if (fieldSelection === null || fieldSelection === undefined) {
      return availableFields;
    }
    return availableFields.filter((field) => fieldSelection.includes(field.key));
  }, [dataRequestEntity]);

  const entityUrl = useMemo(
    () =>
      dataRequestEntity
        ? buildEntityHref(currentWorkspace.workspaceUrl, dataRequestEntity.entityType, dataRequestEntity.entityId)
        : null,
    [dataRequestEntity, currentWorkspace.workspaceUrl],
  );

  if (loading) return <Loading />;
  if (error || !dataRequestEntity) return <ErrorView />;

  return (
    <FieldDataEntry
      key={dataRequestEntityId}
      entityUrl={entityUrl}
      dueDate={dataRequestEntity.dataRequest?.dueDate}
      fieldValues={fieldValues}
      visibleFieldList={visibleFieldList}
      fieldResponseMap={fieldResponseMap}
      workspaceId={currentWorkspace.id}
      dataRequestEntityId={dataRequestEntityId ?? ""}
      documentsRequested={documentsRequested}
      freeTextQuestions={freeTextQuestions}
      initialFreeTextAnswers={dataRequestEntity.freeTextAnswers ?? {}}
      isReadOnly={isReadOnly}
      submitting={submitting}
      onSaveField={saveField}
      onSaveFreeText={saveFreeText}
      onSubmit={handleSubmit}
    />
  );
}
